import { InfrastructureError } from '@goodt-wcore/errors';
import { QueryFilterOperator } from '../utils/query-const';

export class Drilldown {
    /**
     * @type {number}
     * @private
     */
    _index = 0;

    /**
     *
     * @type {string[]}
     */
    dimensions = [];

    /**
     * @type {string}
     */
    name;

    /**
     *
     * @type {import('@goodt-common/data').IQueryFilterInfo[]}
     */
    filters = [];

    /**
     * @return {number}
     */
    get length() {
        return this.dimensions.length;
    }

    /**
     * @return {number}
     */
    get index() {
        return this._index;
    }

    /**
     *
     * @param {number} index
     * @param values
     */
    setIndex(index, values) {
        if (index < 0) {
            throw new RangeError(`Drilldown "${this.name}" index set unavailable`);
        }
        if (index === this._index) {
            return;
        }
        if (index < this._index) {
            this.filters = this.filters.slice(0, index);
            this._index = index;
            return;
        }
        if (values.length < index) {
            throw new RangeError(`Drilldown "${this.name}" index set unavailable: filters=${values} length is less then index=${index}`);
        }
        // if (index > this._index) {
        values.slice(0, index).forEach((value) => {
            this.push(value);
        });
    }

    /**
     *
     * @return {string}
     */
    get dimension() {
        return this.dimensions[this._index];
    }

    constructor(drilldown) {
        const { name, dimensions } = drilldown;
        this.name = name;
        this.dimensions = dimensions;
    }

    /**
     *
     * @param {string|number|boolean} dimensionValue
     */
    push(dimensionValue) {
        if (this.canPush === false) {
            throw new RangeError(`Drilldown "${this.name}" push exceed limit ${this.length}`);
        }
        const filter = {
            name: this.dimension,
            operator: QueryFilterOperator.EQ,
            value: dimensionValue
        };
        this.filters.push(filter);
        this._index += 1;
    }

    pop() {
        if (this.canPop === false) {
            throw new RangeError(`Drilldown "${this.name}" pop unavailable`);
        }
        this.filters.pop();
        this._index -= 1;
    }

    /**
     *
     * @return {boolean}
     */
    get canPop() {
        return this._index > 0;
    }

    /**
     *
     * @return {boolean}
     */
    get canPush() {
        return this._index < this.length - 1;
    }
}

export class DrilldownService {
    /**
     * @type {Record<string, Drilldown>}
     */
    models = {};

    /**
     *
     * @type {Record<string, string>}
     */
    drilldownDimensionsMap = {};

    constructor(drilldowns = []) {
        this.models = Object.fromEntries(drilldowns.map((drilldown) => [drilldown.name, new Drilldown(drilldown)]));
        this.drilldownDimensionsMap = drilldowns.reduce(
            (acc, { name, dimensions: drilldownDimensions }) => ({
                ...acc,
                ...Object.fromEntries(drilldownDimensions.map((dimension) => [dimension, name]))
            }),
            {}
        );
    }

    /**
     *
     * @return {boolean}
     */
    get isActive() {
        return Object.keys(this.models).length > 0;
    }

    /**
     *
     * @return {string[]}
     */
    get activeDimensions() {
        return Object.values(this.models).map(({ dimension }) => dimension);
    }

    /**
     *
     * @return {string[]}
     */
    get names() {
        return Object.keys(this.models);
    }

    /**
     *
     * @return {string[]}
     */
    get dimensions() {
        return Object.values(this.models).flatMap(({ dimensions }) => dimensions);
    }

    /**
     * @param {string} name
     * @return {Drilldown}
     */
    get(name) {
        return this.models[name];
    }

    getDimension(name) {
        return this.get(name).dimension;
    }

    /**
     *
     * @param {string} name
     * @return {boolean}
     */
    has(name) {
        return name in this.models;
    }

    /**
     *
     * @param {string} dimension
     * @return {string|null}
     */
    getNameByDimension(dimension) {
        return this.drilldownDimensionsMap[dimension];
    }

    /**
     *
     * @param {string} name
     * @param {string|number|boolean} value
     * @return {Drilldown}
     */
    push(name, value) {
        const drilldown = this.get(name);
        if (drilldown === undefined) {
            throw new InfrastructureError(`Drilldown "${name}" not exist`);
        }
        drilldown.push(value);

        return drilldown;
    }

    /**
     *
     * @param {string} name
     * @return {Drilldown}
     */
    pop(name) {
        const drilldown = this.get(name);
        if (drilldown === undefined) {
            throw new InfrastructureError(`Drilldown "${name}" not exist`);
        }
        drilldown.pop();

        return drilldown;
    }

    /**
     *
     * @param {string} name
     * @param {number} index
     * @param values
     */
    setIndex(name, index, values = []) {
        const drilldown = this.get(name);
        if (drilldown === undefined) {
            throw new InfrastructureError(`Drilldown "${name}" not exist"`);
        }
        drilldown.setIndex(index, values);

        return drilldown;
    }
    
    /**
     *
     * @param {string} name
     * @return {boolean}
     */
    canPop(name) {
        return this.get(name)?.canPop ?? false;
    }

    /**
     *
     * @param {string} name
     * @return {boolean}
     */
    canPush(name) {
        return this.get(name)?.canPush ?? false;
    }

    /**
     *
     * @return {import('@goodt-common/data').IQueryFilterInfo[]}
     */
    get filters() {
        return Object.values(this.models).flatMap(({ filters }) => filters);
    }
}
